import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  format,
  isSameDay,
  startOfMonth,
} from "date-fns";

export const ARRIVAL = "arrival";
export const DEPARTURE = "departure";

const dayKey = (date) => format(date, "yyyy-MM-dd");

// Booked dates are nights: a booked day cannot be an arrival, but it can be
// the departure day of a stay that ends right before it.
export const isBookedNight = (date, bookedDates = []) =>
  bookedDates.some((bookedDate) => isSameDay(bookedDate, date));

export const getMinimumStay = (date, minimumStayMap = {}) =>
  Math.max(Number(minimumStayMap[dayKey(date)]) || 1, 1);

export const isValidStay = ({
  arrivalDate,
  departureDate,
  bookedDates = [],
  minimumStayMap = {},
}) => {
  if (!arrivalDate || !departureDate) return false;

  const nights = differenceInCalendarDays(departureDate, arrivalDate);
  if (nights < getMinimumStay(arrivalDate, minimumStayMap)) return false;

  return !bookedDates.some(
    (bookedDate) =>
      differenceInCalendarDays(bookedDate, arrivalDate) >= 0 &&
      differenceInCalendarDays(departureDate, bookedDate) >= 1
  );
};

// While picking the departure, a day after the arrival ends the stay and any
// other day starts a new one.
const endsStay = (activeField, date, arrivalDate) =>
  activeField === DEPARTURE &&
  !!arrivalDate &&
  differenceInCalendarDays(date, arrivalDate) > 0;

export const isSelectableDay = ({
  activeField,
  date,
  arrivalDate,
  bookedDates = [],
  minimumStayMap = {},
}) => {
  if (endsStay(activeField, date, arrivalDate)) {
    return isValidStay({
      arrivalDate,
      departureDate: date,
      bookedDates,
      minimumStayMap,
    });
  }

  return !isBookedNight(date, bookedDates);
};

export const resolveDayClick = ({
  activeField,
  date,
  arrivalDate,
  departureDate,
  bookedDates = [],
  minimumStayMap = {},
}) => {
  if (endsStay(activeField, date, arrivalDate)) {
    return {
      arrivalDate,
      departureDate: date,
      activeField: DEPARTURE,
      close: true,
    };
  }

  // Picking the departure before the arrival starts the stay over, while a
  // changed arrival keeps a departure that still works with it.
  const keepDeparture =
    activeField === ARRIVAL &&
    isValidStay({
      arrivalDate: date,
      departureDate,
      bookedDates,
      minimumStayMap,
    });

  return {
    arrivalDate: date,
    departureDate: keepDeparture ? departureDate : null,
    activeField: DEPARTURE,
    close: keepDeparture,
  };
};

export const nightsBetween = (arrivalDate, departureDate) => {
  if (!arrivalDate || !departureDate) return 0;
  const nights = differenceInCalendarDays(departureDate, arrivalDate);

  return nights > 0 ? nights : 0;
};

// Why a day after the arrival cannot be the departure, for its tooltip.
export const departureBlockReason = ({
  arrivalDate,
  date,
  bookedDates = [],
  minimumStayMap = {},
}) => {
  if (!arrivalDate || differenceInCalendarDays(date, arrivalDate) <= 0) {
    return null;
  }

  const crossesBookedNight = bookedDates.some(
    (bookedDate) =>
      differenceInCalendarDays(bookedDate, arrivalDate) >= 0 &&
      differenceInCalendarDays(date, bookedDate) >= 1
  );
  if (crossesBookedNight) return "booked_night";

  const nights = differenceInCalendarDays(date, arrivalDate);
  if (nights < getMinimumStay(arrivalDate, minimumStayMap)) {
    return "minimum_stay";
  }

  return null;
};

// First month a calendar shows: the month of the date, never a past one.
export const clampViewStart = (date, today = new Date()) => {
  const start = startOfMonth(date || today);
  const current = startOfMonth(today);

  return start < current ? current : start;
};

export const isMonthVisible = (date, viewStart, monthsShown = 1) => {
  if (!date || !viewStart) return false;
  const offset = differenceInCalendarMonths(date, viewStart);

  return offset >= 0 && offset < monthsShown;
};
